<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import DeleteDialog from 'dashboard/components-next/ailab/pageComponents/DeleteDialog.vue';
import ConnectInboxDialog from 'dashboard/components-next/ailab/pageComponents/inbox/ConnectInboxDialog.vue';
import ConnectInboxDialogAIGamer from './inboxes/ConnectInboxDialogAIGamer.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const { t } = useI18n();
const dialogType = ref('');
const selectedInbox = ref(null);
const disconnectInboxDialog = ref(null);
const connectInboxDialog = ref(null);

const uiFlags = useMapGetter('ailabInboxes/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingList);
const ailabInboxes = useMapGetter('ailabInboxes/getRecords');

const assistantId = computed(() => props.assistant?.id);
const isAIGamer = computed(
  () => props.assistant?.config?.wlab_sidebar_key === 'ai_gamer'
);

const handleDelete = () => {
  disconnectInboxDialog.value?.dialogRef?.open();
};

const handleCreate = () => {
  dialogType.value = 'create';
  nextTick(() => connectInboxDialog.value?.dialogRef?.open());
};

const handleAction = ({ action, id }) => {
  selectedInbox.value = ailabInboxes.value.find(inbox => id === inbox.id);
  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    }
  });
};

const handleCreateClose = () => {
  dialogType.value = '';
  selectedInbox.value = null;
};

const refreshInboxes = () => {
  if (assistantId.value) {
    store.dispatch('ailabInboxes/get', { assistantId: assistantId.value });
  }
};

onMounted(() => {
  refreshInboxes();
});
</script>

<template>
  <div class="inboxes-tab">
    <!-- Header -->
    <div class="tab-header">
      <div class="header-content">
        <div class="header-icon">
          <span class="i-lucide-inbox size-5" />
        </div>
        <div class="header-text">
          <h2 class="header-title">
            {{ t('AILAB.ASSISTANT_V2.INBOXES_TAB.HEADER.TITLE') }}
          </h2>
          <p class="header-desc">
            {{ t('AILAB.ASSISTANT_V2.INBOXES_TAB.HEADER.DESCRIPTION') }}
          </p>
        </div>
      </div>
      <button class="add-btn" @click="handleCreate">
        <span class="i-lucide-plus size-4" />
        <span>{{ t('AILAB.ASSISTANT_V2.INBOXES_TAB.ACTIONS.CONNECT') }}</span>
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="isFetching" class="loading-container">
      <div class="loading-spinner">
        <span class="i-lucide-loader-2 size-6 animate-spin" />
      </div>
      <p class="loading-text">
        {{ t('AILAB.ASSISTANT_V2.INBOXES_TAB.STATE.LOADING') }}
      </p>
    </div>

    <!-- Empty State -->
    <div v-else-if="!ailabInboxes.length" class="empty-state">
      <div class="empty-icon">
        <span class="i-lucide-inbox size-12" />
      </div>
      <h3 class="empty-title">
        {{ t('AILAB.ASSISTANT_V2.INBOXES_TAB.STATE.EMPTY_TITLE') }}
      </h3>
      <p class="empty-desc">
        {{ t('AILAB.ASSISTANT_V2.INBOXES_TAB.STATE.EMPTY_DESCRIPTION') }}
      </p>
      <button class="empty-btn" @click="handleCreate">
        <span class="i-lucide-plus size-4" />
        <span>{{
          t('AILAB.ASSISTANT_V2.INBOXES_TAB.ACTIONS.CONNECT_FIRST')
        }}</span>
      </button>
    </div>

    <!-- Inboxes List -->
    <div v-else class="inboxes-list">
      <div v-for="inbox in ailabInboxes" :key="inbox.id" class="inbox-card">
        <div class="inbox-info">
          <div class="inbox-icon">
            <span class="i-lucide-message-square size-5" />
          </div>
          <div class="inbox-details">
            <h4 class="inbox-name">{{ inbox.name }}</h4>
            <p class="inbox-type">
              {{
                inbox.channel_type ||
                t('AILAB.ASSISTANT_V2.INBOXES_TAB.LABELS.CHANNEL')
              }}
            </p>
          </div>
        </div>
        <div class="inbox-actions">
          <span class="status-badge active">
            <span class="status-dot" />
            {{ t('AILAB.ASSISTANT_V2.INBOXES_TAB.STATUS.CONNECTED') }}
          </span>
          <button
            class="disconnect-btn"
            @click="handleAction({ action: 'delete', id: inbox.id })"
          >
            <span class="i-lucide-unlink size-4" />
            <span>{{
              t('AILAB.ASSISTANT_V2.INBOXES_TAB.ACTIONS.DISCONNECT')
            }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Info Box -->
    <div class="info-box">
      <span class="i-lucide-info size-4 text-blue-400" />
      <div class="info-content">
        <p class="info-title">
          {{ t('AILAB.ASSISTANT_V2.INBOXES_TAB.INFO.TITLE') }}
        </p>
        <ul class="info-list">
          <li>
            {{ t('AILAB.ASSISTANT_V2.INBOXES_TAB.INFO.ITEMS.AUTO_REPLY') }}
          </li>
          <li>
            {{
              t(
                'AILAB.ASSISTANT_V2.INBOXES_TAB.INFO.ITEMS.USES_PRODUCT_AND_TEMPLATES'
              )
            }}
          </li>
          <li>
            {{
              t(
                'AILAB.ASSISTANT_V2.INBOXES_TAB.INFO.ITEMS.DISCONNECT_ANYTIME'
              )
            }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Delete Dialog -->
    <DeleteDialog
      v-if="selectedInbox"
      ref="disconnectInboxDialog"
      :entity="selectedInbox"
      :delete-payload="{
        assistantId: assistantId,
        inboxId: selectedInbox.id,
      }"
      type="Inboxes"
      @close="handleCreateClose"
    />

    <!-- Connect Inbox Dialog -->
    <ConnectInboxDialog
      v-if="dialogType && !isAIGamer"
      ref="connectInboxDialog"
      :assistant-id="assistantId"
      :type="dialogType"
      @close="handleCreateClose"
    />

    <ConnectInboxDialogAIGamer
      v-if="dialogType && isAIGamer"
      ref="connectInboxDialog"
      :assistant-id="assistantId"
      @close="handleCreateClose"
    />
  </div>
</template>

<style scoped>
.inboxes-tab {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: none;
  --inboxes-surface-bg: rgba(255, 255, 255, 0.03);
  --inboxes-surface-border: rgba(255, 255, 255, 0.08);
  --inboxes-surface-hover: rgba(255, 255, 255, 0.05);
  --inboxes-empty-bg: rgba(255, 255, 255, 0.02);
  --inboxes-empty-border: rgba(255, 255, 255, 0.06);
  --inboxes-empty-icon-bg: rgba(255, 255, 255, 0.05);
  --inboxes-text-primary: #e5e7eb;
  --inboxes-text-secondary: #a8aaae;
  --inboxes-text-muted: #6b6f76;
  --inboxes-action-bg: #ffffff;
  --inboxes-action-text: #000000;
  --inboxes-action-hover-bg: #e5e7eb;
  --inboxes-loading-color: #ffffff;
  --inboxes-info-bg: rgba(59, 130, 246, 0.08);
  --inboxes-info-border: rgba(59, 130, 246, 0.15);
}

:global(body:not(.dark) .inboxes-tab) {
  --inboxes-surface-bg: #ffffff;
  --inboxes-surface-border: rgba(15, 23, 42, 0.1);
  --inboxes-surface-hover: #f8fafc;
  --inboxes-empty-bg: #ffffff;
  --inboxes-empty-border: rgba(15, 23, 42, 0.08);
  --inboxes-empty-icon-bg: #f8fafc;
  --inboxes-text-primary: #111827;
  --inboxes-text-secondary: #4b5563;
  --inboxes-text-muted: #6b7280;
  --inboxes-action-bg: #0066ff;
  --inboxes-action-text: #ffffff;
  --inboxes-action-hover-bg: linear-gradient(135deg, #3388ff 0%, #0066ff 100%);
  --inboxes-loading-color: #0066ff;
  --inboxes-info-bg: rgba(59, 130, 246, 0.06);
  --inboxes-info-border: rgba(59, 130, 246, 0.2);
}

/* Header - Fin AI Card Style */
.tab-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 24px;
  background: var(--inboxes-surface-bg);
  border: 1px solid var(--inboxes-surface-border);
  border-radius: 12px;
}

.header-content {
  display: flex;
  gap: 16px;
  flex: 1;
}

.header-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.15);
  border-radius: 10px;
  color: #60a5fa;
  flex-shrink: 0;
}

.header-text {
  flex: 1;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--inboxes-text-primary);
  margin: 0 0 6px 0;
}

.header-desc {
  font-size: 13px;
  color: var(--inboxes-text-secondary);
  margin: 0;
  line-height: 1.6;
}

.add-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: var(--inboxes-action-bg);
  border: none;
  border-radius: 8px;
  color: var(--inboxes-action-text);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.add-btn:hover {
  background: var(--inboxes-action-hover-bg);
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  background: var(--wlab-bg-tertiary);
  border: 1px solid var(--wlab-border-light);
  border-radius: 12px;
}

.loading-spinner {
  color: var(--inboxes-loading-color);
  margin-bottom: 12px;
}

.loading-text {
  font-size: 13px;
  color: var(--wlab-text-muted);
  margin: 0;
}

/* Empty State - Fin AI Style */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  background: var(--inboxes-empty-bg);
  border: 1px solid var(--inboxes-empty-border);
  border-radius: 12px;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--inboxes-empty-icon-bg);
  border-radius: 16px;
  color: var(--inboxes-text-muted);
  margin-bottom: 20px;
}

.empty-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--inboxes-text-primary);
  margin: 0 0 8px 0;
}

.empty-desc {
  font-size: 14px;
  color: var(--inboxes-text-secondary);
  margin: 0 0 24px 0;
  max-width: 400px;
  line-height: 1.5;
}

.empty-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--inboxes-action-bg);
  border: none;
  border-radius: 8px;
  color: var(--inboxes-action-text);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.empty-btn:hover {
  background: var(--inboxes-action-hover-bg);
}

/* Inboxes List */
.inboxes-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.inbox-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: var(--inboxes-surface-bg);
  border: 1px solid var(--inboxes-surface-border);
  border-radius: 10px;
  transition: all 0.2s;
}

.inbox-card:hover {
  border-color: var(--inboxes-surface-border);
  background: var(--inboxes-surface-hover);
}

.inbox-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.inbox-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 10px;
  color: #3b82f6;
}

.inbox-details {
  display: flex;
  flex-direction: column;
}

.inbox-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--inboxes-text-primary);
  margin: 0 0 4px 0;
}

.inbox-type {
  font-size: 13px;
  color: var(--inboxes-text-muted);
  margin: 0;
  text-transform: capitalize;
}

.inbox-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.active {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.disconnect-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: transparent;
  border: 1px solid var(--wlab-border-default);
  border-radius: 6px;
  color: var(--wlab-text-secondary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.disconnect-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
  color: #ef4444;
}

/* Info Box - Fin AI Style */
.info-box {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  background: var(--inboxes-info-bg);
  border: 1px solid var(--inboxes-info-border);
  border-radius: 10px;
}

.info-content {
  flex: 1;
}

.info-title {
  font-size: 13px;
  font-weight: 600;
  color: #3b82f6;
  margin: 0 0 8px 0;
}

.info-list {
  margin: 0;
  padding-left: 16px;
  font-size: 12px;
  color: var(--wlab-text-muted);
  line-height: 1.6;
}

.info-list li {
  margin-bottom: 4px;
}

.info-list li:last-child {
  margin-bottom: 0;
}
</style>
